/**
 * Типи транзакцій в системі.
 * Використовуються для класифікації та стандартизації фінансових операцій.
 *
 * Деякі типи транзакцій мають підтипи (variations), що представляють більш специфічні операції.
 */
export const TRANSACTION_TYPES = {
  /**
   * Переміщення коштів між рахунками.
   * Використовується для внутрішніх операцій переказу грошей.
   */
  operation: { id: 1, name: 'transactionNames.operation' },

  /**
   * Оплата клієнтом.
   * Представляє різні типи оплат, здійснених клієнтом.
   *
   * service - Оплата послуг клієнтом
   *
   * product - Оплата товарів клієнтом
   *
   * subscription - Оплата абонементів клієнтом
   *
   * giftCard - Оплата сертифікатів клієнтом
   */
  payment: {
    id: 2,
    name: 'transactionNames.payment',
    variations: {
      service: { id: 1, name: 'transactionNames.service' },
      product: { id: 2, name: 'transactionNames.product' },
      subscription: { id: 3, name: 'transactionNames.subscription' },
      giftCard: { id: 4, name: 'transactionNames.card' },
    },
  },

  /**
   * Внесення коштів на рахунок клієнта.
   * Поповнення балансу клієнта різними способами.
   *
   * depositDuringPayment - Внесення коштів під час здійснення покупки
   *
   * depositWithoutPayment - Внесення коштів без прив'язки до покупки
   */
  deposit: {
    id: 3,
    name: 'transactionNames.deposit',
    variations: {
      depositDuringPayment: { id: 1, name: 'transactionNames.deposit' },
      depositWithoutPayment: { id: 2, name: 'transactionNames.deposit' },
    },
  },

  /**
   * Повернення коштів клієнту.
   * Різні типи транзакцій повернення, залежно від причини та способу повернення.
   *
   * refundFromAccount - Повернення клієнту, при якому гроші знімаються з рахунку бізнесу
   *
   * refundToClientAccount - Повернення клієнту, при якому гроші не знімаються з рахунку бізнесу, але поповнюється рахунок клієнта
   *
   * refundBySubscription - Повернення послуг за абонементом
   *
   * refundPaymentInCredit - Повернення коштів при оплаті в кредит
   */
  refund: {
    id: 4,
    name: 'transactionNames.refund',
    variations: {
      refundFromAccount: { id: 1, name: 'transactionNames.refundToClient' },
      refundToClientAccount: { id: 2, name: 'transactionNames.refundToClientAccount' },
      refundBySubscription: { id: 3, name: 'transactionNames.refundBySubscription' },
      refundPaymentInCredit: { id: 4, name: 'transactionNames.refundPaymentInCredit' },
    },
  },

  /**
   * Витрати бізнесу.
   * Операційні витрати, що не пов'язані з основною діяльністю.
   */
  expenses: { id: 5, name: 'transactionNames.expenses' },

  /**
   * Доходи бізнесу.
   * Додаткові надходження, не пов'язані з основними операціями.
   */
  revenue: { id: 6, name: 'transactionNames.revenue' },

  /**
   * Оплата клієнтом через зовнішню платіжну систему.
   * Наприклад, при оплаті карткою через платіжний шлюз.
   */
  paymentWithProvider: {
    id: 7,
    name: 'transactionNames.paymentWithProvider',
  },

  /**
   * Повернення коштів через платіжну систему.
   * Операції повернення коштів із використанням зовнішнього платіжного провайдера.
   *
   * full - Повне повернення всіх коштів
   *
   * partial - Часткове повернення коштів
   */
  refundWithProvider: {
    id: 8,
    name: 'transactionNames.refundWithProvider',
    // TODO: Треба буде подивитись чи взагалі треба ці варіанти
    variations: {
      full: { id: 1, name: 'transactionNames.fullRefundWithProvider' },
      partial: { id: 2, name: 'transactionNames.partialWithProviderByEmployee' },
    },
  },

  /**
   * Зарплата співробітникам.
   * Виплати заробітної плати працівникам компанії.
   *
   * Для використання потрібно вказати:
   *
   * fromAccountId - Ідентифікатор рахунку, з якого видається зарплата
   *
   * employeeId - Ідентифікатор співробітника, якому виплачується зарплата
   */
  salary: { id: 9, name: 'transactionNames.salary' },

  /**
   * Оплата за товари контрагенту.
   * Виплати постачальникам за закуплені товари.
   */
  productsPurchase: { id: 10, name: 'transactionNames.supplier' },

  /**
   * Транспортні витрати на товари.
   * Витрати на доставлення товарів від постачальників.
   */
  transportCosts: { id: 11, name: 'transactionNames.transportCosts' },

  /**
   * Повернення товарів контрагентам.
   * Операції з повернення товарів постачальникам.
   *
   * Наразі ця функціональність не працює і знаходиться в розробці
   */
  productsReturn: { id: 12, name: 'transactionNames.supplierReturn' },

  /**
   * Внесення коштів на рахунок клієнта при активації сертифіката.
   * Транзакція створюється, коли клієнт/співробітник активує сертифікат,
   * і баланс клієнта поповнюється на суму сертифіката.
   */
  activateGiftCard: { id: 13, name: 'transactionNames.certificate' },

  /**
   * Зняття коштів з рахунку клієнта.
   */
  cashOut: { id: 14, name: 'transactionNames.cashOut' },

  /**
   * Нарахування ставки зарплати.
   *
   * На цей час відсутні такі транзакції, потребує перевірки актуальності.
   */
  salaryRate: { id: 15, name: 'transactionNames.salaryRate' },

  /**
   * Нарахування реферальних відсотків.
   *
   * На цей час відсутні такі транзакції, потребує перевірки актуальності.
   */
  referralPercentage: { id: 18, name: 'transactionNames.referral' },

  /**
   * Оплата клієнтом в кредит.
   * Транзакція, що створюється коли клієнт отримує послуги або товари з відтермінуванням оплати.
   * Використовується для обліку кредитних зобов'язань клієнтів.
   *
   * service - Оплата послуг клієнтом у кредит
   *
   * product - Оплата товарів клієнтом у кредит
   *
   * subscription - Оплата абонементів клієнтом у кредит
   *
   * giftCard - Оплата подарункових сертифікатів у кредит
   */
  paymentInCredit: {
    id: 19,
    name: 'transactionNames.paymentInCredit',
    variations: {
      service: { id: 1, name: 'transactionNames.service' },
      product: { id: 2, name: 'transactionNames.product' },
      subscription: { id: 3, name: 'transactionNames.subscription' },
      giftCard: { id: 4, name: 'transactionNames.card' },
    },
  },

  /**
   * Погашення кредитів клієнтом.
   * Транзакції, пов'язані з поверненням клієнтом коштів за раніше надані в кредит
   * послуги, товари або абонементи.
   *
   * repaymentDepositByClientBalance - Погашення кредиту шляхом
   *   наявних коштів на балансі клієнта. Після оплати в кредит,
   *   якщо на рахунку клієнта є гроші.
   *
   * repaymentDepositByMoney - Погашення кредиту шляхом внесення нових коштів клієнтом.
   */
  repaymentDeposit: {
    id: 20,
    name: 'transactionNames.repaymentDeposit',
    variations: {
      // используется когда перед оплатой в кредит у клиента есть деньги на счету
      // этой транзакцией происходит погашение кредита сразу после оплаты в кредит
      repaymentDepositByClientBalance: {
        id: 1,
        name: 'transactionNames.repaymentDepositByClientBalance',
      },
      // погашение кредита деньгами
      repaymentDepositByMoney: { id: 2, name: 'transactionNames.repaymentDeposit' },
    },
  },
} as const
